// A single apportionment item — discriminated union distinguishing cost items from quantum items.
// Order is determined by position in the array (no sortOrder field).
// Labels and caps are derived at render time from context, not stored on the item.
export type ApportionmentItem = (
  | { costs: "ofSettlement" | "towardsPayer" | "generic" }
  | { quantumId: string }
) & {
  enabled: boolean;
  specificAmount?: number; // undefined = max available (waterfall fills it)
};

// Context needed to compute waterfall allocations — caps and settlement amount
export interface ApportionmentContext {
  settlementAmount: number;
  caps: {
    ofSettlement: number;
    towardsPayer: number;
    generic: number;
    quantums: Record<string, number>; // quantumId -> quantum value (only covered quantums have entries)
  };
}

// Result of waterfall computation — used by both client and server
export interface ApportionedAmounts {
  costs: {
    settlement?: number;
    towardsPayer?: number;
    generic?: number;
  };
  quantums: { [quantumId: string]: number };
  total: number;
  remaining: number;
}

// Type guard helpers
export function isCostsItem(item: ApportionmentItem): item is { costs: "ofSettlement" | "towardsPayer" | "generic" } & { enabled: boolean; specificAmount?: number } {
  return "costs" in item;
}

export function isQuantumItem(item: ApportionmentItem): item is { quantumId: string } & { enabled: boolean; specificAmount?: number } {
  return "quantumId" in item;
}

// Get a unique key for an apportionment item (for React keys, lookups)
export function itemKey(item: ApportionmentItem): string {
  if ("costs" in item) return `costs:${item.costs}`;
  return `quantum:${(item as { quantumId: string }).quantumId}`;
}
